/**
 * Design Tokens
 *
 * Centralized design tokens for colors, spacing, typography, etc.
 * These tokens are used by both light and dark themes.
 */

export const colors = {
  // Brand colors
  primary: '#1890ff',
  primaryHover: '#40a9ff',
  primaryActive: '#096dd9',
  primaryBg: '#e6f7ff',

  // Semantic colors
  success: '#52c41a',
  successBg: '#f6ffed',
  warning: '#faad14',
  warningBg: '#fffbe6',
  error: '#ff4d4f',
  errorBg: '#fff2f0',
  info: '#1890ff',
  infoBg: '#e6f7ff',

  // Light theme colors
  light: {
    // Backgrounds
    bg: '#ffffff',
    bgSecondary: '#f5f5f5',
    bgTertiary: '#fafafa',
    bgLayout: '#f0f2f5',

    // Text colors
    text: '#000000d9', // rgba(0, 0, 0, 0.85)
    textSecondary: '#00000073', // rgba(0, 0, 0, 0.45)
    textTertiary: '#00000040', // rgba(0, 0, 0, 0.25)
    textDisabled: '#00000040',

    // Border colors
    border: '#d9d9d9',
    borderSecondary: '#f0f0f0',

    // Interactive states
    hover: '#f5f5f5',
    active: '#e6e6e6',

    // Shadows
    shadow: 'rgba(0, 0, 0, 0.1)',
    shadowMedium: 'rgba(0, 0, 0, 0.15)',
    shadowLarge: 'rgba(0, 0, 0, 0.2)',
  },

  // Dark theme colors
  dark: {
    // Backgrounds
    bg: '#141414',
    bgSecondary: '#1f1f1f',
    bgTertiary: '#252525',
    bgLayout: '#000000',

    // Text colors
    text: '#ffffffd9', // rgba(255, 255, 255, 0.85)
    textSecondary: '#ffffff73', // rgba(255, 255, 255, 0.45)
    textTertiary: '#ffffff40', // rgba(255, 255, 255, 0.25)
    textDisabled: '#ffffff30',

    // Border colors
    border: '#434343',
    borderSecondary: '#303030',

    // Interactive states
    hover: '#262626',
    active: '#303030',

    // Shadows
    shadow: 'rgba(0, 0, 0, 0.45)',
    shadowMedium: 'rgba(0, 0, 0, 0.6)',
    shadowLarge: 'rgba(0, 0, 0, 0.75)',
  },
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
};

export const typography = {
  fontFamily: {
    base: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    code: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
  },
  fontSize: {
    xs: 12,
    sm: 14,
    base: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

export const borderRadius = {
  sm: 2,
  base: 6,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

export const layout = {
  sidebar: {
    widthExpanded: 280, 
    widthCollapsed: 80,
    transition: '0.2s',
  },
  header: {
    height: 64,
  },
  drawer: {
    minWidth: 600,
    defaultWidth: 800,
    maxWidthPercent: 90,
  },
};

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

export const transitions = {
  fast: '0.1s',
  base: '0.2s',
  slow: '0.3s',
  verySlow: '0.5s',
};

// Phase 7: Mobile Responsive Design - Breakpoints
export const breakpoints = {
  xs: 480,   // Mobile phones
  sm: 576,   // Small tablets
  md: 768,   // Tablets
  lg: 992,   // Small laptops
  xl: 1200,  // Desktops
  xxl: 1600, // Large desktops
};

// Media query helpers
export const media = {
  xs: `@media (max-width: ${breakpoints.xs}px)`,
  sm: `@media (max-width: ${breakpoints.sm}px)`,
  md: `@media (max-width: ${breakpoints.md}px)`,
  lg: `@media (max-width: ${breakpoints.lg}px)`,
  xl: `@media (max-width: ${breakpoints.xl}px)`,
  xxl: `@media (max-width: ${breakpoints.xxl}px)`,
  mobile: `@media (max-width: ${breakpoints.md}px)`,    // <= 768px
  tablet: `@media (min-width: ${breakpoints.md + 1}px) and (max-width: ${breakpoints.lg}px)`, // 769-992px
  desktop: `@media (min-width: ${breakpoints.lg + 1}px)`, // > 992px
};
